import { useEffect, useState } from "react";

// Hash routing, no router dependency:
//   #/groups?status=open      #/captures?level=error&source=x
//   #/group/{id}              #/capture/{id}
export type Route =
  | { name: "groups"; params: URLSearchParams }
  | { name: "captures"; params: URLSearchParams }
  | { name: "group"; id: string }
  | { name: "capture"; id: string }
  | { name: "settings" }
  | { name: "internal" };

export const parseHash = (hash: string): Route => {
  const raw = hash.replace(/^#\/?/, "");
  const [pathPart, query = ""] = raw.split("?");
  const segments = pathPart.split("/").filter(Boolean);
  const params = new URLSearchParams(query);
  if (segments[0] === "captures") return { name: "captures", params };
  if (segments[0] === "settings") return { name: "settings" };
  if (segments[0] === "internal") return { name: "internal" };
  if (segments[0] === "capture" && segments[1]) return { name: "capture", id: decodeURIComponent(segments[1]) };
  if (segments[0] === "group" && segments[1]) return { name: "group", id: decodeURIComponent(segments[1]) };
  return { name: "groups", params };
};

export const href = {
  groups: (params?: Record<string, string | undefined>) => `#/groups${toQuery(params)}`,
  captures: (params?: Record<string, string | undefined>) => `#/captures${toQuery(params)}`,
  group: (id: string) => `#/group/${encodeURIComponent(id)}`,
  capture: (id: string) => `#/capture/${encodeURIComponent(id)}`,
  settings: () => `#/settings`,
  internal: () => `#/internal`,
};

const toQuery = (params?: Record<string, string | undefined>) => {
  if (!params) return "";
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) s.set(k, v);
  const q = s.toString();
  return q ? `?${q}` : "";
};

export const navigate = (to: string) => {
  window.location.hash = to;
};

export const useRoute = (): Route => {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
};
